import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { MessageSquareText, MessageSquarePlus, Send, Scale, AlertTriangle, ThumbsUp, ThumbsDown, Loader2, Briefcase, BookOpen, ChevronDown, Calculator, ArrowRight, Compass, LifeBuoy, HeartHandshake, Info, ExternalLink, Dumbbell, Lightbulb, ClipboardCheck } from "lucide-react";
import api from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/StatusBadge";
import { CitationCard } from "@/components/CitationCard";
import { FormattedAnswer } from "@/components/FormattedAnswer";
import { BedrijfsafsprakenMelding } from "@/components/BedrijfsafsprakenMelding";
import { PageHeader } from "@/pages/Kennisbank";
import { toast } from "sonner";

const Melding = ({ icon: Icon, tone, children }) => {
  const tones = {
    blue: "bg-blue-50 border-blue-200 text-blue-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    slate: "bg-slate-100 border-slate-200 text-slate-700",
  };
  return (
    <div className={`flex gap-2.5 items-start border rounded-md p-3 text-sm ${tones[tone]}`}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
};

const InklapSectie = ({ titel, icoon: Icon, children, testId, defaultOpen = false, onOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const gemeld = useRef(false);
  const toggle = () => {
    if (!open && !gemeld.current) {
      gemeld.current = true;
      onOpen?.();
    }
    setOpen(!open);
  };
  return (
    <div className="border border-slate-200 rounded-md bg-white">
      <button data-testid={testId} onClick={toggle}
        className="w-full flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
        <span className="text-left flex-1">{titel}</span>
      </button>
      {open && <div className="px-3.5 pb-3.5 space-y-3">{children}</div>}
    </div>
  );
};

const Answer = ({ data, onFeedback, onFunctiecheck, isLast, followupsRemaining, onSuggest, busy }) => {
  const [gekozenSuggestie, setGekozenSuggestie] = useState(null);
  useEffect(() => {
    if (!busy && gekozenSuggestie !== null) setGekozenSuggestie(null);
  }, [busy, gekozenSuggestie]);
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const navigate = useNavigate();
  const insufficient = data.status === "rood";
  const heeftBron = data.citaties?.length > 0;

  if (data.status === "functiecheck_uitkomst" || data.route === "functiecheck_uitkomst") {
    return (
      <div data-testid="functiecheck-uitkomst-kaart" className="zk-card p-6 animate-fade-up space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl bg-amber-600 flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-white" />
          </span>
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-amber-700">Functiecheck — indicatie</span>
        </div>
        <p className="text-slate-700 leading-relaxed whitespace-pre-line">{data.antwoord}</p>
        {data.vakbond_links?.length > 0 && (
          <div data-testid="vakbond-links" className="border border-slate-200 bg-slate-50 rounded-md p-4">
            <p className="text-sm font-medium text-slate-700 mb-2">Je kunt hiervoor bijvoorbeeld contact opnemen met:</p>
            <ul className="space-y-1.5">
              {data.vakbond_links.map((b, i) => (
                <li key={i} className="text-sm">
                  <a href={b.url} target="_blank" rel="noopener noreferrer" title="Opent in een nieuw tabblad" data-testid={`vakbond-link-${i}`}
                     className="text-blue-700 hover:underline inline-flex items-center gap-1">
                    <ExternalLink className="w-3.5 h-3.5" /> {b.naam}
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 ml-1">{b.type}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
        <Button data-testid="open-salarischeck-btn-uitkomst" onClick={() => navigate("/salaris", { state: { van: "vraag" } })} variant="outline" className="border-slate-300 w-full sm:w-auto">
          <Calculator className="w-4 h-4 mr-2" /> Open Salarischeck
        </Button>
      </div>
    );
  }

  if (data.status === "functiecheck" || data.route === "functiecheck") {
    return (
      <div data-testid="functiecheck-routing" className="zk-card p-6 animate-fade-up space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl bg-amber-600 flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-white" />
          </span>
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-amber-700">Functiecheck</span>
        </div>
        <p className="text-slate-700 leading-relaxed">{data.antwoord}</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button data-testid="open-functiecheck-btn" onClick={() => navigate("/functie", { state: { van: "vraag" } })} className="zk-primary">
            <Briefcase className="w-4 h-4 mr-2" /> Open Functiecheck <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button data-testid="open-salarischeck-btn-secundair" onClick={() => navigate("/salaris", { state: { van: "vraag" } })} variant="outline" className="border-slate-300">
            <Calculator className="w-4 h-4 mr-2" /> Open Salarischeck
          </Button>
        </div>
      </div>
    );
  }

  if (data.status === "salarischeck" || data.route === "salarischeck") {
    return (
      <div data-testid="salarischeck-routing" className="zk-card p-6 animate-fade-up space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl bg-[#4A3122] flex items-center justify-center">
            <Calculator className="w-5 h-5 text-white" />
          </span>
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#B9772B]">Salarischeck</span>
        </div>
        <p className="text-slate-700 leading-relaxed">{data.antwoord}</p>
        <Button data-testid="open-salarischeck-btn" onClick={() => navigate("/salaris", { state: { van: "vraag" } })} className="zk-primary">
          <Calculator className="w-4 h-4 mr-2" /> Open Salarischeck <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="answer-card" className="zk-card p-6 animate-fade-up space-y-4">
      <div className="flex flex-wrap gap-2">
        <StatusBadge status={data.status} />
      </div>

      <div data-testid="answer-text-wrapper" className="prose prose-slate prose-sm max-w-none">
        <FormattedAnswer text={data.antwoord} />
      </div>

      {data.kritische_melding && (
        <div data-testid="kritische-melding">
          <Melding icon={AlertTriangle} tone="amber">{data.kritische_melding}</Melding>
        </div>
      )}

      {data.functiecheck_suggestie && (
        <div data-testid="functiecheck-suggestie" className="bg-secondary border border-border border-l-4 border-l-accent rounded-md p-4 mt-2 space-y-3">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-xl bg-amber-600 flex items-center justify-center shrink-0">
              <ClipboardCheck className="w-4 h-4 text-white" />
            </span>
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-amber-700">Functiecheck</span>
          </div>
          <p className="font-semibold text-primary">Twijfel je over je functie? Doe de Functiecheck</p>
          <p className="text-sm text-muted-foreground">
            Klopt dit niet met wat jij dagelijks doet, of twijfel je of deze functie bij jouw werkzaamheden past? De Functiecheck vergelijkt jouw eigen werkzaamheden met het functiehandboek en geeft een indicatie van de mogelijke functiegroep — geen definitieve inschaling of salarisbepaling.
          </p>
          <Button data-testid="functiecheck-suggestie-btn" onClick={() => onFunctiecheck(data)} className="zk-primary w-full sm:w-auto h-auto py-2.5 whitespace-normal">
            <Briefcase className="w-4 h-4 mr-2 shrink-0" /> <span className="min-w-0">Doe de Functiecheck</span>
          </Button>
        </div>
      )}

      {data.tilcheck_suggestie && (
        <div data-testid="tilcheck-suggestie" className="border border-cyan-200 bg-cyan-50 rounded-md p-4">
          <p className="text-sm text-cyan-900 mb-3">
            🏋️ Wil je een indicatie van de fysieke belasting in jouw situatie? Vul onze Tilcheck in — die helpt knelpunten inzichtelijk te maken en het gesprek voor te bereiden.
          </p>
          <Button data-testid="tilcheck-suggestie-btn" onClick={() => navigate("/tilcheck", { state: { van: "vraag" } })} variant="outline" size="sm" className="border-cyan-400 text-cyan-900 hover:bg-cyan-100">
            <Dumbbell className="w-4 h-4 mr-2" /> Doe de Tilcheck
          </Button>
        </div>
      )}

      {isLast && data.vervolgvraag_suggesties?.length > 0 && followupsRemaining > 0 && (
      <InklapSectie testId="sectie-vervolgvragen" icoon={MessageSquarePlus} titel="Meer vragen over dit onderwerp"
        onOpen={() => api.post("/qa/sectieklap", { sectie: "vervolgvragen", audit_id: data.audit_id }).catch(() => {})}>
        <div data-testid="vervolgvraag-suggesties">
          <div className="flex flex-col gap-2">
            {data.vervolgvraag_suggesties.slice(0, 3).map((s, i) => {
              const gekozen = gekozenSuggestie === i;
              const bezig = gekozen && busy;
              const geblokkeerd = gekozenSuggestie !== null;
              return (
                <button key={i} data-testid={`vervolgvraag-suggestie-${i}`} disabled={geblokkeerd}
                  onClick={() => { setGekozenSuggestie(i); onSuggest(s); }}
                  className={`text-sm text-left leading-snug w-full max-w-full break-words border rounded-lg px-3 py-2 transition-colors flex items-start gap-2 ${
                    bezig ? "border-[#B9772B] bg-[#FBF3E3] text-[#5B3A22] cursor-wait"
                    : geblokkeerd ? "border-[#E4D3B8] bg-white opacity-50 cursor-not-allowed"
                    : "border-[#D8BE95] bg-white hover:bg-[#FBF3E3] hover:border-[#B9772B]"}`}>
                  {bezig && <Loader2 className="w-4 h-4 mt-0.5 shrink-0 animate-spin text-[#B9772B]" />}
                  <span className="min-w-0">{s}</span>
                </button>
              );
            })}
          </div>
          {gekozenSuggestie !== null && busy && (
            <p data-testid="vervolgvraag-bezig-melding" className="text-xs text-[#8a5a20] mt-2">
              Je vervolgvraag wordt verwerkt — het antwoord verschijnt hieronder.
            </p>
          )}
        </div>
      </InklapSectie>
      )}

      {(data.wettelijke_melding || !insufficient || data.ondersteuning_verwijzing || data.calamiteiten_context) && (
      <InklapSectie testId="sectie-aandachtspunten" icoon={Scale} titel="Belangrijke aandachtspunten"
        onOpen={() => api.post("/qa/sectieklap", { sectie: "aandachtspunten", audit_id: data.audit_id }).catch(() => {})}>
        {data.wettelijke_melding && (
          <div data-testid="wettelijke-melding">
            <Melding icon={Scale} tone="blue">{data.wettelijke_melding}</Melding>
          </div>
        )}
        {!insufficient && (
          <div data-testid="qa-bedrijfsafspraken">
            <BedrijfsafsprakenMelding />
          </div>
        )}
        {data.calamiteiten_context && (
          <div data-testid="calamiteiten-context" className="border border-amber-200 bg-amber-50 rounded-md p-3.5 flex gap-2.5">
            <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800"><span className="font-medium">Goed om te weten: </span>{data.calamiteiten_context}</p>
          </div>
        )}
        {data.ondersteuning_verwijzing && (
          <div data-testid="ondersteuning-verwijzing" className="border border-slate-200 bg-slate-50 rounded-md p-3.5 flex gap-2.5">
            <HeartHandshake className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600">{data.ondersteuning_verwijzing}</p>
          </div>
        )}
      </InklapSectie>
      )}

      {(data.arbo_escalatie || data.arbo_meldpunten || data.privacy_meldpunten || data.basis_meldroute || data.hulp) && (
      <InklapSectie testId="sectie-hulp-meldroutes" icoon={LifeBuoy} titel="Hulp en meldroutes" defaultOpen={Boolean(data.arbo_escalatie || (data.basis_meldroute && !data.basis_meldroute.compact))}
        onOpen={() => api.post("/qa/sectieklap", { sectie: "hulp_meldroutes", audit_id: data.audit_id }).catch(() => {})}>

      {data.basis_meldroute && (data.basis_meldroute.compact ? (
        <p data-testid="basis-meldroute-compact" className="text-sm text-slate-700 border border-slate-200 bg-slate-50 rounded-md p-3">
          {data.basis_meldroute.compact}
        </p>
      ) : (
        <div data-testid="basis-meldroute" className="border border-slate-200 bg-slate-50 rounded-md p-4">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-3">
            <LifeBuoy className="w-4 h-4" /> {data.basis_meldroute.titel}
          </p>
          <ol className="space-y-2.5">
            {data.basis_meldroute.punten.map((p, i) => (
              <li key={i} data-testid={`basis-meldroute-punt-${i}`} className="flex gap-2.5">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">
                    {p.url ? (
                      <a href={p.url} target="_blank" rel="noopener noreferrer" title="Opent in een nieuw tabblad" className="inline-flex items-center gap-1 hover:underline">
                        {p.naam} <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : p.naam}
                  </p>
                  <p className="text-sm text-slate-700">{p.toelichting}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      ))}

      {data.arbo_escalatie && (
        <div data-testid="arbo-escalatie" className="border border-amber-300 bg-amber-50 rounded-md p-4">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-900 mb-2">
            <AlertTriangle className="w-4 h-4" /> {data.arbo_escalatie.titel}
          </p>
          <p className="text-sm text-amber-900 mb-3">{data.arbo_escalatie.inleiding}</p>
          <ol className="space-y-2">
            {data.arbo_escalatie.stappen.map((s, i) => (
              <li key={i} data-testid={`arbo-escalatie-stap-${i}`} className="flex gap-2.5">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                <p className="text-sm text-amber-900 min-w-0">{s}</p>
              </li>
            ))}
          </ol>
          <p className="text-xs text-amber-800 mt-3 border-t border-amber-200 pt-2">{data.arbo_escalatie.toelichting}</p>
        </div>
      )}

      {data.arbo_meldpunten && (
        <div data-testid="arbo-meldpunten" className="border border-emerald-200 bg-emerald-50 rounded-md p-4">
          <p className="flex items-center gap-2 text-sm font-semibold text-emerald-900 mb-3">
            <LifeBuoy className="w-4 h-4" /> {data.arbo_meldpunten.titel}
          </p>
          <ol className="space-y-2.5">
            {data.arbo_meldpunten.punten.map((p, i) => (
              <li key={i} data-testid={`arbo-meldpunt-${i}`} className="flex gap-2.5">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-emerald-900">
                    {p.url ? (
                      <a href={p.url} target="_blank" rel="noopener noreferrer" title="Opent in een nieuw tabblad" className="inline-flex items-center gap-1 hover:underline">
                        {p.naam} <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : p.naam}
                  </p>
                  <p className="text-sm text-emerald-800">{p.toelichting}</p>
                  {p.links && (
                    <p className="mt-0.5 space-x-3">
                      {p.links.map((l, j) => (
                        <a key={j} href={l.url} target="_blank" rel="noopener noreferrer" title="Opent in een nieuw tabblad" data-testid={`arbo-meldpunt-link-${i}-${j}`}
                           className="text-xs text-emerald-700 underline inline-flex items-center gap-1">
                          {l.naam} <ExternalLink className="w-3 h-3" />
                        </a>
                      ))}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
          <p className="text-[11px] text-emerald-700 mt-3 border-t border-emerald-200 pt-2">Onderbouwing: {data.arbo_meldpunten.bron}</p>
        </div>
      )}

      {data.privacy_meldpunten && (
        <div data-testid="privacy-meldpunten" className="border border-blue-200 bg-blue-50 rounded-md p-4">
          <p className="flex items-center gap-2 text-sm font-semibold text-blue-900 mb-3">
            <LifeBuoy className="w-4 h-4" /> {data.privacy_meldpunten.titel}
          </p>
          <ol className="space-y-2.5">
            {data.privacy_meldpunten.punten.map((p, i) => (
              <li key={i} data-testid={`privacy-meldpunt-${i}`} className="flex gap-2.5">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-blue-900">
                    {p.url ? (
                      <a href={p.url} target="_blank" rel="noopener noreferrer" title="Opent in een nieuw tabblad" className="inline-flex items-center gap-1 hover:underline">
                        {p.naam} <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : p.naam}
                  </p>
                  <p className="text-sm text-blue-800">{p.toelichting}</p>
                </div>
              </li>
            ))}
          </ol>
          <p className="text-[11px] text-blue-700 mt-3 border-t border-blue-200 pt-2">Onderbouwing: {data.privacy_meldpunten.bron}</p>
        </div>
      )}

      {data.hulp && (
        <div data-testid="hulp-ondersteuning" className="border border-blue-100 bg-blue-50/40 rounded-md p-4"
          onClickCapture={(e) => {
            const a = e.target.closest("a");
            if (a) api.post("/qa/hulpklik", { categorie: data.hulp.categorie, naam: a.textContent, url: a.href, audit_id: data.audit_id }).catch(() => {});
          }}>
          <p className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-1 flex items-center gap-1.5">
            <LifeBuoy className="w-3.5 h-3.5" /> {data.hulp.titel}
          </p>
          <p className="text-xs text-slate-500 mb-2.5">{data.hulp.intro}</p>
          <div className="space-y-2.5">
            {data.hulp.lijnen.map((lijn, i) => (
              <div key={i}>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-0.5">{lijn.label}</p>
                <ul className="space-y-0.5">
                  {lijn.items.map((it, j) => (
                    <li key={j} className="text-sm text-slate-700 flex gap-1.5 items-baseline">
                      <span className="text-blue-300">•</span>
                      <span>
                        {it.url ? (
                          <a href={it.url} target="_blank" rel="noopener noreferrer" title="Opent in een nieuw tabblad" className="text-blue-700 hover:underline inline-flex items-center gap-1">{it.naam} <ExternalLink className="w-3 h-3" /></a>
                        ) : it.naam}
                        {it.detail && <span className="text-xs text-slate-400"> — {it.detail}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
      </InklapSectie>
      )}

      {(heeftBron || data.gebruikte_versies?.length > 0) && (
      <InklapSectie testId="toon-bron-btn" icoon={BookOpen} titel="Waar staat dat?"
        onOpen={() => api.post("/qa/sectieklap", { sectie: "bronnen", audit_id: data.audit_id }).catch(() => {})}>
        <div data-testid="bron-details" className="space-y-3">
          {data.citaties?.length > 0 ? (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Uit de cao, wet en het functiehandboek</p>
              <div className="space-y-2">
                {data.citaties.map((c, i) => <CitationCard key={i} citation={c} index={i} />)}
              </div>
            </div>
          ) : (
            <p data-testid="geen-passende-bron" className="text-sm text-slate-500 italic">
              Voor dit onderdeel is geen passende bron in de broncollectie beschikbaar.
            </p>
          )}

          {data.gebruikte_versies?.length > 0 && (
            <p className="text-[11px] text-slate-400">Gebruikte versies: {data.gebruikte_versies.join(" · ")} · AI-versie: {data.ai_versie}</p>
          )}
        </div>
      </InklapSectie>
      )}

      <div className="border-t border-slate-200 pt-3 flex items-center gap-3">
        {!feedbackGiven ? (
          <>
            <span className="text-sm text-slate-600">Was dit antwoord nuttig?</span>
            <button data-testid="feedback-ja" onClick={() => { onFeedback(true); setFeedbackGiven(true); }} className="inline-flex items-center gap-1 text-sm border border-slate-300 rounded-md px-3 py-1.5 hover:bg-emerald-50 hover:border-emerald-300 transition-colors">
              <ThumbsUp className="w-4 h-4" /> Ja
            </button>
            <button data-testid="feedback-nee" onClick={() => { onFeedback(false); setFeedbackGiven(true); }} className="inline-flex items-center gap-1 text-sm border border-slate-300 rounded-md px-3 py-1.5 hover:bg-red-50 hover:border-red-300 transition-colors">
              <ThumbsDown className="w-4 h-4" /> Nee
            </button>
          </>
        ) : (
          <span data-testid="feedback-thanks" className="text-sm text-emerald-700">Bedankt voor je feedback.</span>
        )}
      </div>
    </div>
  );
};

const MAX_VERVOLGVRAGEN = 5;

// Sessiestatus voorbeeldvragen-paneel: overleeft navigatie binnen de app, reset bij paginavernieuwing.
let voorbeeldenOpenSessie = false;

// Tijdelijke vraag/antwoord-status (sessieopslag, per gebruiker): overleeft doorklikken
// naar tools en de browser-terugknop, verdwijnt bij het sluiten van het tabblad.
const qaKey = (uid) => `zk_qa_state_${uid || "anon"}`;
const leesQaState = (uid) => {
  try { return JSON.parse(sessionStorage.getItem(qaKey(uid))); } catch { return null; }
};

const VraagAntwoord = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [vraag, setVraag] = useState(() => leesQaState(user?.id)?.vraag || "");
  const [gesprek, setGesprek] = useState(() => leesQaState(user?.id)?.gesprek || []);
  const [busy, setBusy] = useState(false);
  const [showOrExamples, setShowOrExamples] = useState(false);
  const [nieuwAntwoord, setNieuwAntwoord] = useState(false);
  const [toonVoorbeelden, setToonVoorbeelden] = useState(voorbeeldenOpenSessie);
  const wisselVoorbeelden = () => {
    voorbeeldenOpenSessie = !toonVoorbeelden;
    setToonVoorbeelden(voorbeeldenOpenSessie);
  };
  const geklikteSuggestie = useRef(null);
  const inputRef = useRef(null);
  const laatsteTurnRef = useRef(null);

  const kiesSuggestie = (ex) => {
    geklikteSuggestie.current = ex;
    setVraag(ex);
  };

  const welkomKey = `zk_welkom_gezien_${user?.id || "anon"}`;
  const [welkomGezien, setWelkomGezien] = useState(() => {
    try { return localStorage.getItem(welkomKey) === "1"; } catch { return false; }
  });
  const markeerWelkomGezien = () => {
    if (welkomGezien) return;
    try { localStorage.setItem(welkomKey, "1"); } catch { /* ignore */ }
    setWelkomGezien(true);
  };

  // Herbereken per gebruiker: bij wisselen van gebruiker (welkomKey verandert) opnieuw uit localStorage lezen.
  useEffect(() => {
    try { setWelkomGezien(localStorage.getItem(welkomKey) === "1"); } catch { setWelkomGezien(false); }
  }, [welkomKey]);

  const vervolgvragenGesteld = Math.max(0, gesprek.length - 1);
  const followupsRemaining = MAX_VERVOLGVRAGEN - vervolgvragenGesteld;
  const heeftGesprek = gesprek.length > 0;

  const stelVraag = async (tekst) => {
    const q = (tekst ?? vraag).trim();
    if (!q) return;
    // Limiet bereikt? Dan start de volgende vraag automatisch een nieuw gesprek.
    const startNieuw = heeftGesprek && followupsRemaining <= 0;
    setBusy(true);
    const geschiedenis = startNieuw ? [] : gesprek.map((t) => ({ vraag: t.vraag, antwoord: t.data.antwoord }));
    try {
      const res = await api.post("/qa/ask", { vraag: q, geschiedenis });
      if (!heeftGesprek && geklikteSuggestie.current === q) {
        api.post("/qa/suggestieklik", { suggestie: q, status: res.data.status, audit_id: res.data.audit_id }).catch(() => {});
      }
      geklikteSuggestie.current = null;
      // Scrollpositie bepalen vóór het antwoord de pagina verlengt.
      const bijnaOnderaan = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 250;
      if (startNieuw) {
        setGesprek([{ vraag: q, data: res.data }]);
        toast.info(`Nieuw gesprek gestart — het maximum van ${MAX_VERVOLGVRAGEN} vervolgvragen was bereikt.`);
      } else {
        setGesprek((g) => [...g, { vraag: q, data: res.data }]);
      }
      setVraag("");
      markeerWelkomGezien();
      if (startNieuw || bijnaOnderaan) {
        setTimeout(() => laatsteTurnRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
      } else {
        setNieuwAntwoord(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Er ging iets mis.");
    } finally { setBusy(false); }
  };

  const sendFeedback = (turn) => async (nuttig) => {
    await api.post("/feedback", { audit_id: turn.data?.audit_id, vraag: turn.vraag, nuttig });
  };

  const goToFunctiecheck = (data) => {
    navigate("/functie", {
      state: {
        functienaam: data.functiecheck_zoekterm || "",
        beschrijving: data.functiecheck_vraag || "",
        van: "vraag",
      },
    });
  };

  const nieuwGesprek = () => {
    setGesprek([]);
    setVraag("");
    setNieuwAntwoord(false);
    voorbeeldenOpenSessie = false;
    setToonVoorbeelden(false);
    setShowOrExamples(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => inputRef.current?.focus(), 300);
  };

  // Status bewaren zodat doorklikken naar een tool (of de terugknop) niets wist;
  // leeg gesprek + lege invoer = opgeslagen status opruimen (nieuw gesprek/reset).
  useEffect(() => {
    try {
      if (gesprek.length === 0 && !vraag) {
        sessionStorage.removeItem(qaKey(user?.id));
      } else {
        const scrollY = leesQaState(user?.id)?.scrollY || 0;
        sessionStorage.setItem(qaKey(user?.id), JSON.stringify({ vraag, gesprek, scrollY }));
      }
    } catch { /* ignore */ }
  }, [vraag, gesprek, user?.id]);

  // Mobiel: body-class zodat de footer ruimte reserveert boven de vaste "Nieuwe vraag stellen"-balk.
  useEffect(() => {
    if (gesprek.length > 0) {
      document.body.classList.add("zk-mobiele-vraagbalk");
      return () => document.body.classList.remove("zk-mobiele-vraagbalk");
    }
  }, [gesprek.length > 0]);

  // Scrollpositie terugzetten bij herstel; vastleggen bij het verlaten van de pagina.
  useEffect(() => {
    const s = leesQaState(user?.id);
    if (s?.gesprek?.length && s.scrollY > 0) {
      setTimeout(() => window.scrollTo({ top: s.scrollY }), 120);
    }
    return () => {
      try {
        const raw = sessionStorage.getItem(qaKey(user?.id));
        if (raw) {
          const st = JSON.parse(raw);
          st.scrollY = window.scrollY;
          sessionStorage.setItem(qaKey(user?.id), JSON.stringify(st));
        }
      } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Indicator automatisch verbergen zodra de gebruiker zelf onderaan komt.
  useEffect(() => {
    if (!nieuwAntwoord) return;
    const h = () => {
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 150) setNieuwAntwoord(false);
    };
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, [nieuwAntwoord]);

  const examples = [
    "Mag mijn rooster zomaar veranderen?",
    "Hoeveel overwerk mag ik maken?",
    "Wanneer krijg ik ploegentoeslag?",
    "Wat moet ik doen bij een arbeidsongeval?",
    "Hoeveel vakantiedagen heb ik per jaar?",
    "Mijn leidinggevende schreeuwt tegen me, wat kan ik doen?",
    "Hoe werkt tijd voor tijd?",
    "Is mijn tilwerk te zwaar?",
    "Mag mijn werkgever mij verplicht overplaatsen?",
  ];

  const orExamples = [
    "Waarvoor heeft de OR instemmingsrecht?",
    "Wanneer geldt het adviesrecht van de OR?",
    "Wat zijn de taken van een VGWM-commissie?",
    "Wanneer moet de OR betrokken worden?",
  ];

  return (
    <div className={`animate-fade-up ${gesprek.length > 0 ? "pb-16 md:pb-0" : ""}`}>
      <PageHeader title="Vraag & antwoord" icon={MessageSquareText}
        accent={{ chip: "bg-[#B9772B]", bar: "from-[#D8A24A] to-[#5B3A22]", text: "text-[#B9772B]" }}
        subtitle="Stel je vraag in gewone taal. De app zoekt uitsluitend in de cao, de wet en het functiehandboek en laat altijd zien waar het staat. Je kunt tot vijf vervolgvragen stellen om je antwoord compleet te maken." />

      {gesprek.length > 0 && (
        <div className="hidden md:flex sticky top-16 z-20 justify-end mb-3">
          <Button data-testid="nieuwe-vraag-boven-btn" onClick={nieuwGesprek} variant="outline"
            className="bg-white/95 backdrop-blur border-[#B9772B] text-[#B9772B] hover:bg-[#FBF3E3] hover:text-[#8a5a20] shadow-sm rounded-full">
            <MessageSquarePlus className="w-4 h-4 mr-2" /> Nieuwe vraag stellen
          </Button>
        </div>
      )}

      {/* Mobiel: vaste balk direct boven de mobiele navigatie (portal: buiten de
          getransformeerde pagina-container, anders werkt position:fixed niet) */}
      {gesprek.length > 0 && createPortal(
        <div data-testid="nieuwe-vraag-mobiel-balk"
          className="md:hidden fixed left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-[#EBE1D2] px-4 py-2 bottom-[calc(8.5rem+env(safe-area-inset-bottom))]">
          <Button data-testid="nieuwe-vraag-mobiel-btn" onClick={nieuwGesprek} variant="outline"
            className="w-full border-[#B9772B] text-[#B9772B] hover:bg-[#FBF3E3] hover:text-[#8a5a20] rounded-full h-9">
            <MessageSquarePlus className="w-4 h-4 mr-2" /> Nieuwe vraag stellen
          </Button>
        </div>,
        document.body
      )}

      {gesprek.length > 0 && (
        <div data-testid="gesprek" className="space-y-4 mb-6">
          {gesprek.map((turn, i) => (
            <div key={i} ref={i === gesprek.length - 1 ? laatsteTurnRef : null} data-testid={`gesprek-turn-${i}`} className="space-y-2">
              <div className="flex justify-end">
                <div data-testid={`gesprek-vraag-${i}`} className="bg-[#4A3122] text-white text-sm rounded-2xl rounded-br-sm px-4 py-2 max-w-[80%]">
                  {turn.vraag}
                </div>
              </div>
              <Answer
                data={turn.data}
                onFeedback={sendFeedback(turn)}
                onFunctiecheck={goToFunctiecheck}
                isLast={i === gesprek.length - 1}
                followupsRemaining={followupsRemaining}
                onSuggest={(s) => stelVraag(s)}
                busy={busy}
              />
            </div>
          ))}
        </div>
      )}

      {!heeftGesprek && !welkomGezien && (
        <div data-testid="welkom-kaart" className="mb-6 max-w-3xl mx-auto rounded-xl border border-[#E4D3B8] bg-gradient-to-br from-[#FBF3E3] to-white p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="shrink-0 mt-0.5 w-10 h-10 rounded-lg bg-[#B9772B] flex items-center justify-center">
              <Compass className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-[#4A3122] mb-2">Welkom bij Zoetwaren Kompas</h2>
              <div className="space-y-2 text-sm text-[#5B3A22] leading-relaxed">
                <p>Zoetwaren Kompas helpt werknemers de juiste richting te vinden bij vragen over de CAO, Arbo, OR/WOR en functiegroepen.</p>
                <p>Antwoorden zijn gebaseerd op goedgekeurde wettelijke bronnen, cao-bronnen en andere gevalideerde vakinhoudelijke documenten.</p>
                <p>Het systeem geeft geen bindend advies en neemt geen beslissingen over individuele situaties. Het helpt bij het vinden van relevante informatie, artikelen en regelingen en wijst waar nodig op mogelijke vervolgstappen en gesprekspartners.</p>
                <p className="font-medium text-[#4A3122]">Zoetwaren Kompas geeft niet het antwoord op alles, maar helpt wel de weg te vinden naar het juiste antwoord.</p>
              </div>
              <button data-testid="welkom-functiehandboek-link" onClick={() => navigate("/functiehandboek")}
                className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-[#8a5a20] bg-white border border-[#E4D3B8] rounded-md px-3 py-2 hover:bg-[#FBF3E3] transition-colors">
                <BookOpen className="w-4 h-4 text-[#B9772B]" />
                Bekijk het Functiehandboek — alle officiële functies en werkvloernamen
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="zk-card p-4 mb-6">
        {heeftGesprek && (
          <div data-testid="vervolgvraag-teller" className="text-xs text-slate-500 mb-2">
            {followupsRemaining > 0
              ? `Vervolgvraag stellen (nog ${followupsRemaining} van ${MAX_VERVOLGVRAGEN} over)`
              : `Maximum van ${MAX_VERVOLGVRAGEN} vervolgvragen bereikt — je volgende vraag start automatisch een nieuw gesprek.`}
          </div>
        )}
        <Textarea
          ref={inputRef}
          data-testid="vraag-input"
          value={vraag}
          onChange={(e) => setVraag(e.target.value)}
          spellCheck={true}
          lang="nl"
          placeholder={heeftGesprek && followupsRemaining > 0 ? "Stel een vervolgvraag…" : heeftGesprek ? "Stel een nieuwe vraag…" : "Bijv. Hoeveel pauze heb ik recht op bij een dienst van 9 uur?"}
          className="min-h-[90px] border-slate-300 resize-none"
        />
        <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
          {!heeftGesprek ? (
            <div className="w-full">
              <button data-testid="toggle-voorbeeldvragen" onClick={wisselVoorbeelden}
                className="text-xs text-slate-500 hover:text-slate-900 border border-slate-200 rounded-full px-3 py-1.5 hover:border-slate-400 transition-colors inline-flex items-center gap-1.5">
                <Lightbulb className="w-3.5 h-3.5 text-[#B9772B]" />
                Voorbeeldvragen
                <ChevronDown className={`w-3 h-3 transition-transform ${toonVoorbeelden ? "rotate-180" : ""}`} />
              </button>
              {toonVoorbeelden && (
                <div data-testid="voorbeeldvragen-paneel" className="mt-2">
                  <div className="flex flex-wrap gap-2">
                    {examples.map((ex) => (
                      <button key={ex} data-testid="example-question" onClick={() => kiesSuggestie(ex)} className="text-xs text-slate-500 hover:text-slate-900 border border-slate-200 rounded-full px-2.5 py-1 hover:border-slate-400 transition-colors">
                        {ex}
                      </button>
                    ))}
                    <button data-testid="toggle-or-voorbeelden" onClick={() => setShowOrExamples((v) => !v)}
                      className="text-xs text-slate-400 hover:text-slate-700 border border-dashed border-slate-300 rounded-full px-2.5 py-1 hover:border-slate-400 transition-colors inline-flex items-center gap-1">
                      Voor OR- en commissieleden
                      <ChevronDown className={`w-3 h-3 transition-transform ${showOrExamples ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                  {showOrExamples && (
                    <div data-testid="or-voorbeelden" className="flex flex-wrap gap-2 mt-2">
                      {orExamples.map((ex) => (
                        <button key={ex} data-testid="example-question-or" onClick={() => kiesSuggestie(ex)} className="text-xs text-slate-500 hover:text-slate-900 border border-slate-200 rounded-full px-2.5 py-1 hover:border-slate-400 transition-colors">
                          {ex}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <button data-testid="nieuw-gesprek-btn" onClick={nieuwGesprek} className="text-xs text-slate-500 hover:text-slate-900 border border-slate-200 rounded-full px-2.5 py-1 hover:border-slate-400 transition-colors">
              Nieuw gesprek starten
            </button>
          )}
          <Button data-testid="vraag-submit-btn" onClick={() => stelVraag()} disabled={busy} className="zk-primary ml-auto">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            {busy ? "Kompas raadplegen..." : heeftGesprek && followupsRemaining > 0 ? "Vervolgvraag stellen" : "Vraag stellen"}
          </Button>
        </div>
      </div>

      {nieuwAntwoord && (
        <button
          data-testid="nieuw-antwoord-indicator"
          onClick={() => {
            laatsteTurnRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            setNieuwAntwoord(false);
          }}
          className="fixed bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 z-30 bg-[#4A3122] text-white text-sm px-4 py-2 rounded-full shadow-lg flex items-center gap-1.5 hover:bg-[#5B3A22] transition-colors animate-fade-up"
        >
          Nieuw antwoord <ChevronDown className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default VraagAntwoord;
